import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Mail, ArrowLeft, CheckCircle, Loader2, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [error, setError] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/auth/forgot-password', { email });
      setSent(true);
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setResetting(true);
    try {
      await api.post('/auth/reset-password', {
        token: resetToken,
        new_password: newPassword
      });
      setResetComplete(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#050505]">
      <div className="w-full flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img src="/logo.png" alt="Assagart" className="h-10 w-10" />
            <span className="font-serif text-xl text-white">Assagart</span>
          </Link>

          {resetComplete ? (
            <div className="text-center" data-testid="reset-complete">
              <div className="inline-flex p-4 bg-emerald-400/10 rounded-full mb-6">
                <CheckCircle size={32} className="text-emerald-400" />
              </div>
              <h1 className="font-serif text-3xl text-white mb-4">Password Reset</h1>
              <p className="text-muted-foreground mb-8">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <Link to="/login">
                <Button className="btn-primary w-full" data-testid="back-to-login-btn">
                  Sign In
                </Button>
              </Link>
            </div>
          ) : sent && resetToken ? (
            <div data-testid="reset-form">
              <div className="inline-flex p-3 bg-gold/10 rounded-full mb-6">
                <KeyRound size={24} className="text-gold" />
              </div>
              <h1 className="font-serif text-3xl text-white mb-2">Set New Password</h1>
              <p className="text-muted-foreground mb-8">
                Enter your new password below.
              </p>

              {error && (
                <div className="p-3 mb-6 bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                    New Password
                  </Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="new-password-input"
                    className="luxury-input w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                    Confirm Password
                  </Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="confirm-password-input"
                    className="luxury-input w-full"
                  />
                </div>
                <Button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={resetting}
                  data-testid="reset-submit-btn"
                >
                  {resetting ? (
                    <>
                      <Loader2 size={16} className="animate-spin mr-2" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            </div>
          ) : sent ? (
            <div className="text-center" data-testid="reset-sent">
              <div className="inline-flex p-4 bg-gold/10 rounded-full mb-6">
                <Mail size={32} className="text-gold" />
              </div>
              <h1 className="font-serif text-3xl text-white mb-4">Check Your Email</h1>
              <p className="text-muted-foreground mb-8">
                If an account exists for <span className="text-white">{email}</span>, 
                we've sent password reset instructions to that address.
              </p>
              <Link to="/login">
                <Button className="btn-secondary w-full" data-testid="back-to-login-btn">
                  <ArrowLeft size={16} className="mr-2" />
                  Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <div data-testid="forgot-password-form">
              <h1 className="font-serif text-3xl text-white mb-2">Forgot Password?</h1>
              <p className="text-muted-foreground mb-8">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {error && (
                <div className="p-3 mb-6 bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleRequestReset} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="forgot-email-input"
                    className="luxury-input w-full"
                  />
                </div>
                <Button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>

              <p className="mt-8 text-center text-sm text-muted-foreground">
                Remember your password?{' '}
                <Link to="/login" className="text-gold hover:underline" data-testid="back-to-login-link">
                  Sign In
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
